import { fmtDiaCurto, fmtKgBR, fmtMoedaBR } from "@/lib/formato-mercado";
import { ChipReferencial } from "./status-chip";
import type { OfertaPublicaDTO } from "@shared/mercado-ofertas";

/**
 * O CONTEÚDO ANÔNIMO de uma oferta — variedade, volume estimado, % de primeira,
 * quando fica disponível, e o preço da praça como REFERÊNCIA.
 *
 * Um componente só para o link público (`/o/:token`) e para a tela Praça do
 * comprador, porque é a MESMA informação: duas versões divergiriam na primeira
 * linha nova, e a do link — que é a que o comprador de verdade usa — seria a
 * esquecida.
 *
 * ═══ A OFERTA NÃO TEM PREÇO (059) ═══════════════════════════════════════════
 *
 * A linha "O da praça no dia da carga" vem ANTES da cotação e é a frase que
 * manda. A cotação de hoje entra embaixo, com data e etiqueta, como referência
 * para o comprador decidir se vale a viagem — não como valor combinado. O ciclo
 * anterior congelava o preço na publicação e prometia, para uma carga do dia
 * 15, o preço do dia 9.
 *
 * ⚠️ Nada aqui calcula. Volume e % vêm prontos do servidor.
 */
export function BlocoOferta({ oferta, compacto }: { oferta: OfertaPublicaDTO; compacto?: boolean }) {
  // A praça publica UMA Nanica, sem classificação — escrever "100% de 1ª" nela
  // seria um número que não quer dizer nada para quem lê. Mesma decisão do
  // texto da chamada (`mensagens-oferta.ts`).
  const ehNanica = oferta.variedade === "NANICA";

  return (
    <div className="space-y-3">
      <div>
        <p className={`font-mono font-extrabold tabular-nums ${compacto ? "text-2xl" : "text-3xl"}`} data-testid="texto-kg-oferta">
          ~{fmtKgBR(oferta.volumeKg)}
        </p>
        <p className="text-sm font-semibold">{rotuloVariedade(oferta.variedade)}</p>
        {!ehNanica && (
          <p className="text-xs text-muted-foreground" data-testid="texto-pct-primeira">
            cerca de {oferta.pctPrimeira.toLocaleString("pt-BR", { maximumFractionDigits: 0 })}% de 1ª
          </p>
        )}
      </div>

      <p className="text-xs">
        <span className="text-muted-foreground">Disponível a partir de </span>
        <b>{fmtDiaCurto(oferta.disponivelEm)}</b>
      </p>

      <div className="rounded-lg border bg-muted/40 px-3 py-2" data-testid="bloco-preco-oferta">
        <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">Preço</p>
        <p className="text-sm font-semibold" data-testid="texto-preco-do-dia-da-carga">
          O da praça no dia da carga
        </p>
        {oferta.cotacao && (
          <>
            <p className="mt-1.5 text-[11px] text-muted-foreground flex flex-wrap items-center gap-1.5">
              Hoje, {fmtDiaCurto(oferta.cotacao.data)}
              <ChipReferencial referencial={oferta.cotacao.referencial} testId="chip-referencial-oferta" />
            </p>
            <ul className="mt-1 space-y-0.5">
              {oferta.cotacao.itens.map(i => (
                <li key={i.qualidade ?? "unica"} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">{i.qualidade ? `${i.qualidade}ª` : rotuloVariedade(oferta.variedade)}</span>
                  <span className="font-mono tabular-nums font-semibold">{fmtMoedaBR(i.precoKg)}<span className="text-[11px] font-normal text-muted-foreground">/kg</span></span>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {/* A ressalva não é rodapé jurídico: é o que evita a discussão no pátio. */}
      <p className="text-[11px] text-muted-foreground">
        Kg e classificação finais no romaneio.
      </p>
    </div>
  );
}

export function rotuloVariedade(variedade: string): string {
  return variedade === "NANICA" ? "Nanica" : variedade === "PRATA" ? "Prata" : variedade;
}
